import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { scrollToSection } from "./Reveal";

const LINKS = [
  { label: "Services", href: "#services" },
  { label: "Work", href: "#work" },
  { label: "Assessment", href: "#assessment" },
  { label: "Pricing", href: "#pricing" },
  { label: "Journal", href: "#journal" },
  { label: "Contact", href: "#contact" },
];

export function LogoMark({ size = 30 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" aria-hidden="true">
      <rect width="64" height="64" rx="14" fill="#1d1d1f" />
      <text x="32" y="42" fontFamily="Georgia, 'Times New Roman', serif" fontSize="28" fontWeight="500" fill="#ffffff" textAnchor="middle" letterSpacing="1">SB</text>
    </svg>
  );
}

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (e, href) => {
    e.preventDefault();
    setOpen(false);
    scrollToSection(href);
  };

  return (
    <header className={`fixed top-0 inset-x-0 z-50 glass-nav ${scrolled ? "shadow-[0_1px_12px_rgba(0,0,0,0.05)]" : ""}`}>
      <nav className="max-w-7xl mx-auto px-4 sm:px-8 h-14 flex items-center justify-between" aria-label="Main">
        <a href="#top" data-testid="nav-logo" onClick={(e) => go(e, "#top")} className="flex items-center gap-2.5">
          <LogoMark />
          <span className="text-[15px] font-semibold tracking-tight text-[#1d1d1f]">
            Sundeepa Bamval
            <span className="hidden sm:inline text-[#86868b] font-normal"> — Fashion Design Consultancy</span>
          </span>
        </a>

        <div className="hidden md:flex items-center gap-7">
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.href.slice(1)}`}
              onClick={(e) => go(e, l.href)}
              className="link-underline text-[13px] font-medium text-[#1d1d1f]/80 hover:text-[#1d1d1f] transition-colors"
            >
              {l.label}
            </a>
          ))}
          <a
            href="#booking"
            data-testid="nav-cta-booking"
            onClick={(e) => go(e, "#booking")}
            className="px-4 py-1.5 rounded-full bg-[#0066cc] hover:bg-[#0077ed] text-white text-[13px] font-medium transition-all active:scale-[0.97]"
          >
            Book a Call
          </a>
        </div>

        <button
          className="md:hidden p-2 -mr-2 text-[#1d1d1f]"
          data-testid="nav-mobile-toggle"
          aria-label={open ? "Close menu" : "Open menu"}
          onClick={() => setOpen(!open)}
        >
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.19, 1, 0.22, 1] }}
            className="md:hidden overflow-hidden glass-nav border-t border-black/5"
          >
            <div className="px-6 py-4 flex flex-col gap-1">
              {LINKS.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={(e) => go(e, l.href)}
                  className="py-2.5 text-[15px] font-medium text-[#1d1d1f] border-b border-black/5 last:border-0"
                >
                  {l.label}
                </a>
              ))}
              <a
                href="#booking"
                onClick={(e) => go(e, "#booking")}
                className="mt-2 mb-1 px-4 py-3 rounded-full bg-[#0066cc] text-white text-center text-sm font-medium"
              >
                Book a Call
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
